import type { Dispatch, SetStateAction } from 'react';
import React, { useContext, createContext, useMemo, useState } from 'react';

import useGetFreelancerPeriodJobs from 'state/remote/remote-freelancer/use-get-freelancer-period-jobs';

import type { PeriodJob } from 'state/remote/types';

type FreelancerPeriodJobs = {
  data: Array<PeriodJob>;
  isLoading?: boolean;
  error: Error | null;
  refetch: (options: {
    throwOnError?: boolean;
    cancelRefetch?: boolean;
  }) => unknown;
};

type FreelancerPeriodJobsContext = {
  periodJobs: FreelancerPeriodJobs;
  filterEnaled: boolean;
  handleFilterEnabled: (arg: boolean) => void;
  setAlertMessage: Dispatch<SetStateAction<string>>;
  setMinimumPay: Dispatch<SetStateAction<string>>;
  setDistance: Dispatch<SetStateAction<string>>;
  alertMessage: string;
  minimumPay: string;
  distance: string;
};

const FreelancerPeriodJobsInitialValue = {
  alertMessage: '',
  distance: '',
  filterEnaled: false,
  handleFilterEnabled: () => {},
  minimumPay: '',
  periodJobs: {
    data: [] as Array<PeriodJob>,
    error: null,
    refetch: () => {},
  },
  setAlertMessage: () => '',
  setDistance: () => '',
  setMinimumPay: () => 0,
};

const FreelancerPeriodJobsContext = createContext<FreelancerPeriodJobsContext>(
  FreelancerPeriodJobsInitialValue,
);

const FreelancerPeriodJobsContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [alertMessage, setAlertMessage] = useState('');
  const [filterEnaled, setFilterEnabled] = useState(false);
  const [minimumPay, setMinimumPay] = useState('');
  const [distance, setDistance] = useState('');
  const [filterInitiated, setFilterInitiated] = useState(false);

  const handleFilterEnabled = (enable: boolean) => {
    setFilterEnabled(enable);
    if (enable) {
      const filters = {
        min_rate: minimumPay,
        travel_distance: distance,
      };
      localStorage.setItem('PeriodJobsfilter', JSON.stringify(filters));
    } else {
      localStorage.removeItem('PeriodJobsfilter');
      setMinimumPay('');
      setDistance('');
      setFilterInitiated(false);
    }
  };
  // check if there is a filter stored in local Storage => then initiate states with it.
  // this operation have to occure just once. for that we added filterInitiated state.
  const filters = localStorage.getItem('PeriodJobsfilter');
  if (!filterInitiated && filters) {
    setFilterInitiated(true);
    setFilterEnabled(true);
    const filterObj = JSON.parse(filters);
    setMinimumPay(filterObj.min_rate);
    setDistance(filterObj.travel_distance);
  }

  const periodjobs = useGetFreelancerPeriodJobs(
    filterEnaled,
    minimumPay,
    distance,
    filterInitiated,
  );

  const periodJobsContext = useMemo((): FreelancerPeriodJobs => {
    return periodjobs;
  }, [periodjobs]);

  return (
    <FreelancerPeriodJobsContext.Provider
      value={{
        alertMessage,
        distance,
        filterEnaled,
        handleFilterEnabled,
        minimumPay,
        periodJobs: periodJobsContext,
        setAlertMessage,
        setDistance,
        setMinimumPay,
      }}
    >
      {children}
    </FreelancerPeriodJobsContext.Provider>
  );
};

export const useFreelancerPeriodJobs = (): FreelancerPeriodJobsContext =>
  useContext(FreelancerPeriodJobsContext);

export default {
  Consumer: FreelancerPeriodJobsContext.Consumer,
  Provider: FreelancerPeriodJobsContextProvider,
};
